import { Auth } from "aws-amplify";

const STORAGE_KEY = "chocolate-admin.recentUserIds";
const LEGACY_STORAGE_KEY = "chocolate-admin.recentUsers";
const MAX_RECENT_USERS = 5;
const MAX_AGE_MS = 8 * 60 * 60 * 1000;

type RecentUserEntry = {
  id: string;
  openedAt: number;
};

type RecentUserStore = Record<string, RecentUserEntry[]>;

const purgeLegacyRecentUsers = () => {
  try {
    localStorage.removeItem(LEGACY_STORAGE_KEY);
  } catch {
    // Ignore storage failures.
  }
};

const readStore = (): RecentUserStore => {
  purgeLegacyRecentUsers();
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return {};
    const parsed = JSON.parse(stored);
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
      return {};
    }
    return parsed as RecentUserStore;
  } catch {
    return {};
  }
};

const writeStore = (store: RecentUserStore) => {
  try {
    if (Object.keys(store).length === 0) {
      localStorage.removeItem(STORAGE_KEY);
      return;
    }
    localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
  } catch {
    // Ignore storage failures so opening a profile still works.
  }
};

const toEntry = (value: unknown): RecentUserEntry | null => {
  if (!value || typeof value !== "object") return null;
  const entry = value as RecentUserEntry;
  if (typeof entry.id !== "string" || entry.id.trim() === "") return null;
  if (typeof entry.openedAt !== "number" || !Number.isFinite(entry.openedAt)) {
    return null;
  }
  return { id: entry.id, openedAt: entry.openedAt };
};

const freshEntries = (entries: unknown, now: number): RecentUserEntry[] => {
  if (!Array.isArray(entries)) return [];
  return entries
    .map(toEntry)
    .filter((entry): entry is RecentUserEntry => entry !== null)
    .filter((entry) => now - entry.openedAt < MAX_AGE_MS)
    .slice(0, MAX_RECENT_USERS);
};

export const getSignedInAdminId = async (): Promise<string | null> => {
  try {
    const user = await Auth.currentAuthenticatedUser();
    const sub = user?.signInUserSession?.idToken?.payload?.sub;
    if (typeof sub === "string" && sub.trim() !== "") return sub;
    return null;
  } catch {
    return null;
  }
};

export const readRecentUserIds = (
  adminId: string,
  now = Date.now()
): string[] => {
  if (!adminId) return [];
  const store = readStore();
  const entries = freshEntries(store[adminId], now);
  const stored = Array.isArray(store[adminId]) ? store[adminId] : [];
  if (JSON.stringify(stored) !== JSON.stringify(entries)) {
    if (entries.length === 0) delete store[adminId];
    else store[adminId] = entries;
    writeStore(store);
  }
  return entries.map((entry) => entry.id);
};

export const rememberRecentUser = (
  adminId: string,
  userId: string,
  now = Date.now()
) => {
  if (!adminId || !userId) return;
  const store = readStore();
  const existing = freshEntries(store[adminId], now).filter(
    (entry) => entry.id !== userId
  );
  store[adminId] = [{ id: userId, openedAt: now }, ...existing].slice(
    0,
    MAX_RECENT_USERS
  );
  writeStore(store);
};

export const forgetRecentUser = (adminId: string, userId: string) => {
  if (!adminId || !userId) return;
  const store = readStore();
  const remaining = freshEntries(store[adminId], Date.now()).filter(
    (entry) => entry.id !== userId
  );
  if (remaining.length === 0) delete store[adminId];
  else store[adminId] = remaining;
  writeStore(store);
};

export const clearRecentUsers = () => {
  try {
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(LEGACY_STORAGE_KEY);
  } catch {
    // Ignore storage failures during sign-out.
  }
};
